import { useCallback, useContext, useMemo, useState } from 'react';
import { DataRoomShareableLink } from 'models/ShareableLink';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

export type UseShareableLinkActionProps = {
  link: DataRoomShareableLink;
  updateShareableLink?: (link: DataRoomShareableLink) => void;
};

const useChangeShareableLinkState = (props: UseShareableLinkActionProps) => {
  const { link, updateShareableLink } = props;

  const { _id, isActive, linkExpiresDate } = link;

  const [isLoading, setIsLoading] = useState(false);

  const { doesntHaveAccess } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const isActiveLink = useMemo(() => {
    const now = +new Date();
    const expiresDate = +new Date(linkExpiresDate);

    return isActive && now < expiresDate;
  }, [isActive, linkExpiresDate]);

  const changeState = useCallback(async () => {
    if (doesntHaveAccess) return;

    try {
      setIsLoading(true);

      const { data } =
        await dataRoomApi.shareableLinks.changeShareableLinkState(
          _id,
          !isActiveLink,
        );

      updateShareableLink?.({ ...link, ...data });
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    _id,
    isActiveLink,
    doesntHaveAccess,
    link,
    updateShareableLink,
    popServerError,
  ]);

  return {
    changeState,
    isActiveLink,
    isChangeStateLoading: isLoading,
    isChangeStateDisabled: isLoading || doesntHaveAccess,
  };
};

export default useChangeShareableLinkState;
